import { useEffect } from 'react';
import { Shield, Eye, Lock, Users, Database, Globe } from 'lucide-react';

const Privacy = () => {
  useEffect(() => {
    document.title = 'Privacy Policy - FemSportNet';
  }, []);

  const principles = [
    {
      icon: <Shield className="h-6 w-6 text-green-500" />,
      title: 'Data Protection',
      description: 'We implement industry-standard security measures to protect your personal information.'
    },
    {
      icon: <Eye className="h-6 w-6 text-green-500" />,
      title: 'Transparency',
      description: 'We clearly explain what data we collect, how we use it, and who we share it with.'
    },
    {
      icon: <Lock className="h-6 w-6 text-green-500" />,
      title: 'User Control',
      description: 'You have control over your data and can access, update, or delete it at any time.'
    },
    {
      icon: <Users className="h-6 w-6 text-green-500" />,
      title: 'Minimal Collection',
      description: 'We only collect data that is necessary to provide and improve our services.'
    }
  ];

  return (
    <div className="bg-white dark:bg-gray-900 min-h-screen transition-colors duration-200">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-green-600 to-green-800 py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Privacy Policy
            </h1>
            <p className="text-xl text-green-100 mb-8">
              Your privacy is important to us. Learn how we collect, use, and protect your personal information.
            </p>
            <p className="text-green-100">
              Last updated: March 15, 2024
            </p>
          </div>
        </div>
      </section>

      {/* Privacy Principles */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-8 text-center">
              Our Privacy Principles
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
              {principles.map((principle, index) => (
                <div key={index} className="text-center">
                  <div className="inline-flex items-center justify-center rounded-full bg-green-100 dark:bg-green-900 p-4 mb-4">
                    {principle.icon}
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                    {principle.title}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-300 text-sm">
                    {principle.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Policy Content */}
      <section className="py-16 bg-gray-50 dark:bg-gray-800">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="bg-white dark:bg-gray-700 rounded-lg shadow p-8 space-y-8">
              
              {/* Information We Collect */}
              <div>
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4 flex items-center">
                  <Database className="h-6 w-6 text-green-500 mr-2" />
                  Information We Collect
                </h2>
                
                <div className="space-y-4">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                      Information You Provide
                    </h3>
                    <ul className="list-disc list-inside text-gray-600 dark:text-gray-300 space-y-1">
                      <li>Account information (name, email, password)</li>
                      <li>Profile information (bio, sports, achievements, photos)</li>
                      <li>Content you create (posts, messages, comments)</li>
                      <li>Communication with our support team</li>
                      <li>Payment information for marketplace transactions</li>
                    </ul>
                  </div>
                  
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                      Information We Collect Automatically
                    </h3>
                    <ul className="list-disc list-inside text-gray-600 dark:text-gray-300 space-y-1">
                      <li>Device information (IP address, browser type, operating system)</li>
                      <li>Usage data (pages visited, features used, time spent)</li>
                      <li>Location data (if you enable location services)</li>
                      <li>Cookies and similar tracking technologies</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* How We Use Information */}
              <div>
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                  How We Use Your Information
                </h2>
                
                <ul className="list-disc list-inside text-gray-600 dark:text-gray-300 space-y-2">
                  <li>Provide and maintain our services</li>
                  <li>Personalize your experience and content</li>
                  <li>Connect you with other athletes and opportunities</li>
                  <li>Process marketplace transactions</li>
                  <li>Send important updates and notifications</li>
                  <li>Improve our platform and develop new features</li>
                  <li>Ensure platform security and prevent fraud</li>
                  <li>Comply with legal obligations</li>
                </ul>
              </div>

              {/* Information Sharing */}
              <div>
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                  How We Share Information
                </h2>
                
                <div className="space-y-4">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                      Public Information
                    </h3>
                    <p className="text-gray-600 dark:text-gray-300">
                      Your profile information, posts, and achievements are visible to other users 
                      as part of the social networking features of our platform.
                    </p>
                  </div>
                  
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                      Service Providers
                    </h3>
                    <p className="text-gray-600 dark:text-gray-300">
                      We share information with trusted third-party service providers who help us 
                      operate our platform, process payments, and provide customer support.
                    </p>
                  </div>
                  
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                      Legal Requirements
                    </h3>
                    <p className="text-gray-600 dark:text-gray-300">
                      We may disclose information when required by law or to protect our rights, 
                      safety, or the rights and safety of others.
                    </p>
                  </div>
                </div>
              </div>

              {/* Data Security */}
              <div>
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                  Data Security
                </h2>
                
                <p className="text-gray-600 dark:text-gray-300 mb-4">
                  We implement appropriate technical and organizational measures to protect your 
                  personal information against unauthorized access, alteration, disclosure, or destruction.
                </p>
                
                <ul className="list-disc list-inside text-gray-600 dark:text-gray-300 space-y-1">
                  <li>Encryption of data in transit and at rest</li>
                  <li>Regular security assessments and updates</li>
                  <li>Access controls and authentication measures</li>
                  <li>Employee training on data protection</li>
                  <li>Incident response procedures</li>
                </ul>
              </div>

              {/* Your Rights */}
              <div>
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                  Your Rights and Choices
                </h2>
                
                <ul className="list-disc list-inside text-gray-600 dark:text-gray-300 space-y-2">
                  <li><strong>Access:</strong> Request a copy of your personal information</li>
                  <li><strong>Update:</strong> Correct or update your information through your profile</li>
                  <li><strong>Delete:</strong> Request deletion of your account and personal data</li>
                  <li><strong>Portability:</strong> Request your data in a portable format</li>
                  <li><strong>Opt-out:</strong> Unsubscribe from marketing communications</li>
                  <li><strong>Restrict:</strong> Limit how we process your information</li>
                </ul>
              </div>

              {/* International Transfers */}
              <div>
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4 flex items-center">
                  <Globe className="h-6 w-6 text-green-500 mr-2" />
                  International Data Transfers
                </h2>
                
                <p className="text-gray-600 dark:text-gray-300">
                  Your information may be transferred to and processed in countries other than your own. 
                  We ensure appropriate safeguards are in place to protect your data in accordance with 
                  applicable privacy laws, including GDPR and CCPA.
                </p>
              </div>

              {/* Data Retention */}
              <div>
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                  Data Retention
                </h2>
                
                <p className="text-gray-600 dark:text-gray-300">
                  We retain your personal information for as long as necessary to provide our services 
                  and fulfill the purposes outlined in this policy. When you delete your account, we will 
                  delete your personal information within 30 days, except where we are required to retain 
                  it for legal or regulatory purposes.
                </p>
              </div>

              {/* Children's Privacy */}
              <div>
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                  Children's Privacy
                </h2>
                
                <p className="text-gray-600 dark:text-gray-300">
                  Our services are not intended for children under 13 years of age. We do not knowingly 
                  collect personal information from children under 13. If we become aware that we have 
                  collected personal information from a child under 13, we will take steps to delete 
                  such information.
                </p>
              </div>

              {/* Changes to Policy */}
              <div>
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                  Changes to This Policy
                </h2>
                
                <p className="text-gray-600 dark:text-gray-300">
                  We may update this Privacy Policy from time to time. We will notify you of any material 
                  changes by posting the new policy on this page and updating the "Last updated" date. 
                  We encourage you to review this policy periodically.
                </p>
              </div>

              {/* Contact Information */}
              <div>
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                  Contact Us
                </h2>
                
                <p className="text-gray-600 dark:text-gray-300 mb-4">
                  If you have any questions about this Privacy Policy or our data practices, please contact us:
                </p>
                
                <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
                  <p className="text-gray-600 dark:text-gray-300">
                    <strong>Email:</strong> privacy@femsportnet.com<br />
                    <strong>Address:</strong> 123 Tech Street, Suite 400, San Francisco, CA 94105<br />
                    <strong>Phone:</strong> +1 (555) 123-4567
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Privacy;